/**
 * Pancake Tower — pure, deterministic game logic.
 * No DOM, no timers: the component drives it with step(dt) and drop().
 * World units: x grows right, layers stack upward by index.
 */

export type Rng = () => number;

export const CONFIG = {
  /** logical world width; the canvas scales this to the screen */
  worldWidth: 360,
  layerHeight: 22,
  baseWidth: 200,
  /** slide speed (world units / s) */
  baseSpeed: 130,
  maxDtMs: 50,
  /** |dx| at or below this (world units) counts as PERFECT */
  perfectTolerance: 5,
  /** perfect bonus = min(streak, cap) on top of the 1 point per layer */
  perfectBonusCap: 3,
  /** from this streak length on, every perfect regrows the layer */
  regrowStreak: 3,
  regrowAmount: 5,
  speedPerLayer: 4,
  maxSpeed: 620,
  /** stack top is kept at this fraction of the view height (from the bottom) */
  cameraTopFraction: 0.55,
  /** debris physics (world units, seconds) */
  gravity: 900,
  debrisHop: 70,
  debrisDrift: 45,
  debrisSpin: 2.6,
};

export type Topping = 'plain' | 'butter' | 'syrup' | 'berries';
const TOPPINGS: Topping[] = ['plain', 'butter', 'syrup', 'berries'];

export interface Span {
  x: number;
  w: number;
}

export interface Layer extends Span {
  topping: Topping;
}

export interface Moving extends Span {
  dir: 1 | -1;
  speed: number;
}

export interface GameState {
  stack: Layer[];
  current: Moving;
  score: number;
  layers: number;
  perfects: number;
  trims: number;
  streak: number;
  bestStreak: number;
  over: boolean;
  elapsedMs: number;
  rng: Rng;
}

/** Small seedable PRNG so tests (and replays) are deterministic. */
export function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function createGame(rng: Rng = Math.random): GameState {
  const base: Layer = { x: (CONFIG.worldWidth - CONFIG.baseWidth) / 2, w: CONFIG.baseWidth, topping: 'plain' };
  return {
    stack: [base],
    current: { x: 0, w: base.w, dir: 1, speed: CONFIG.baseSpeed },
    score: 0,
    layers: 0,
    perfects: 0,
    trims: 0,
    streak: 0,
    bestStreak: 0,
    over: false,
    elapsedMs: 0,
    rng,
  };
}

export function step(g: GameState, dtMs: number): GameState {
  if (g.over) return g;
  const dt = Math.min(dtMs, CONFIG.maxDtMs);
  g.elapsedMs += dt;
  const c = g.current;
  const maxX = CONFIG.worldWidth - c.w;
  c.x += c.dir * c.speed * (dt / 1000);
  if (c.x > maxX) {
    c.x = Math.max(0, 2 * maxX - c.x);
    c.dir = -1;
  } else if (c.x < 0) {
    c.x = Math.min(maxX, -c.x);
    c.dir = 1;
  }
  return g;
}

export interface Cut extends Span {
  /** which side of the stack the piece fell off: 1 = right, -1 = left */
  side: 1 | -1;
}

export interface DropResult {
  kind: 'none' | 'perfect' | 'trim' | 'miss';
  cut?: Cut;
}

function pickTopping(rng: Rng): Topping {
  return TOPPINGS[Math.min(TOPPINGS.length - 1, Math.floor(rng() * TOPPINGS.length))];
}

function spawnNext(g: GameState): void {
  const top = g.stack[g.stack.length - 1];
  const dir: 1 | -1 = g.current.dir === 1 ? -1 : 1;
  g.current = {
    x: dir === 1 ? 0 : CONFIG.worldWidth - top.w,
    w: top.w,
    dir,
    speed: Math.min(CONFIG.maxSpeed, CONFIG.baseSpeed + g.layers * CONFIG.speedPerLayer),
  };
}

export function drop(g: GameState): DropResult {
  if (g.over) return { kind: 'none' };
  const c = g.current;
  const top = g.stack[g.stack.length - 1];
  const dx = c.x - top.x;
  const left = Math.max(c.x, top.x);
  const right = Math.min(c.x + c.w, top.x + top.w);
  const overlap = right - left;

  if (overlap <= 0) {
    g.over = true;
    return { kind: 'miss', cut: { x: c.x, w: c.w, side: dx > 0 ? 1 : -1 } };
  }

  let result: DropResult;
  if (Math.abs(dx) <= CONFIG.perfectTolerance) {
    g.streak += 1;
    g.perfects += 1;
    g.bestStreak = Math.max(g.bestStreak, g.streak);
    let { x, w } = top;
    if (g.streak >= CONFIG.regrowStreak && w < CONFIG.baseWidth) {
      const grow = Math.min(CONFIG.regrowAmount, CONFIG.baseWidth - w);
      w += grow;
      x = Math.min(Math.max(0, x - grow / 2), CONFIG.worldWidth - w);
    }
    g.stack.push({ x, w, topping: pickTopping(g.rng) });
    g.score += Math.min(g.streak, CONFIG.perfectBonusCap);
    result = { kind: 'perfect' };
  } else {
    g.streak = 0;
    g.trims += 1;
    g.stack.push({ x: left, w: overlap, topping: pickTopping(g.rng) });
    const cut: Cut = dx > 0
      ? { x: right, w: c.w - overlap, side: 1 }
      : { x: c.x, w: c.w - overlap, side: -1 };
    result = { kind: 'trim', cut };
  }
  g.layers += 1;
  g.score += 1;
  spawnNext(g);
  return result;
}

export function getStats(g: GameState): Record<string, number> {
  return { layers: g.layers, perfects: g.perfects, bestStreak: g.bestStreak, trims: g.trims };
}

export interface Debris extends Span {
  /** bottom of the piece, height above the plate */
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  topping: Topping;
}

/** A trimmed overhang that hops a little, tips toward its side and falls away. */
export function makeDebris(cut: Cut, layerIndex: number, topping: Topping, reducedMotion = false): Debris {
  return {
    x: cut.x,
    w: cut.w,
    y: layerIndex * CONFIG.layerHeight,
    vx: cut.side * CONFIG.debrisDrift,
    vy: reducedMotion ? 0 : CONFIG.debrisHop,
    rot: 0,
    vr: reducedMotion ? 0 : cut.side * CONFIG.debrisSpin,
    topping,
  };
}

export function stepDebris(d: Debris, dtMs: number): Debris {
  const dt = Math.min(dtMs, CONFIG.maxDtMs) / 1000;
  d.vy -= CONFIG.gravity * dt;
  d.y += d.vy * dt;
  d.x += d.vx * dt;
  d.rot += d.vr * dt;
  return d;
}

/** Camera offset (height above the plate shown at the bottom of the view). */
export function cameraTarget(stackCount: number, viewHeight: number): number {
  return Math.max(0, stackCount * CONFIG.layerHeight - viewHeight * CONFIG.cameraTopFraction);
}

/** Frame-rate independent exponential easing. rate = 1/s. */
export function approach(current: number, target: number, dtMs: number, rate: number): number {
  return target + (current - target) * Math.exp(-rate * (dtMs / 1000));
}
